import type { LocalDate } from '@cadencia/contracts';
import { addLocalDays } from '../units/local-date.js';
import { REVIEW_WINDOWS } from './review.js';
import { performanceTrend, type PerformanceTrend } from './volume-response.js';

/**
 * ¿Funcionó lo que se aplicó? (§10 y §11 del encargo, E5 del diagnóstico).
 *
 * EL HUECO QUE ESTO CIERRA
 *
 * `training_decisions` tiene tres columnas para esta pregunta —`evaluate_after`,
 * `outcome` y `outcome_evaluated_at`— desde la Fase 3, y las tres se escribían
 * SIEMPRE en `null` (`decisionsRepo.ts`). El motor registraba lo que decidía y
 * no comprobaba nunca si había servido. Es la trampa de `overrideBiasPct` en
 * `CLAUDE.md`: un campo guardado, sincronizado y con sitio en el contrato, y
 * nadie que lo produzca.
 *
 * LO QUE ESTO NO AFIRMA, y es lo más importante del fichero
 *
 * No afirma causas. El §10 es explícito: «reconoce que factores externos no
 * medidos pueden explicar cambios; no atribuyas automáticamente todo el
 * resultado al programa», y «una mejora de e1RM no demuestra por sí sola
 * crecimiento muscular». Por eso los veredictos se llaman «asociado a» y no
 * «causó», y por eso `inconclusive` es un resultado de primera clase y no un
 * error: la mayoría de las veces, con los datos de una persona, es la respuesta
 * correcta.
 *
 * Tampoco mide hipertrofia. Mide RENDIMIENTO OBSERVADO en los ejercicios del
 * músculo afectado, que es lo único que la aplicación registra de verdad. El
 * §10 separa las dos cosas y este módulo se queda en su lado.
 */

// ---------------------------------------------------------------------------
// Cuándo se evalúa
// ---------------------------------------------------------------------------

/**
 * Semanas de exposición antes de juzgar un cambio.
 *
 * Es la MISMA ventana que `REVIEW_WINDOWS.volumeSettleWeeks`, y no por
 * comodidad: la revisión semanal no vuelve a subir el volumen de un músculo
 * hasta que el cambio anterior «se ha podido medir», y eso y «ya se puede
 * evaluar» son la misma afirmación. Dos números para la misma idea acabarían
 * diciendo cosas distintas del mismo cambio.
 */
export const OUTCOME_EXPOSURE_WEEKS = REVIEW_WINDOWS.volumeSettleWeeks;

/**
 * Cuándo toca evaluar una decisión aplicada. `null` si no es evaluable.
 *
 * Solo `volume_change` por ahora. Es la decisión cuya respuesta es la
 * información individual más útil —cuánto volumen tolera ESTA persona en ESTE
 * músculo— y la única cuya respuesta se puede leer en el rendimiento de unos
 * ejercicios concretos. Las demás no se evalúan todavía, y se dice: una
 * descarga se juzga por la recuperación, no por el e1RM, y juzgarla con la
 * vara equivocada daría un veredicto plausible y falso.
 */
export function evaluateAfterFor(
  decisionType: string,
  appliedOn: LocalDate,
): LocalDate | null {
  if (decisionType !== 'volume_change') return null;
  return addLocalDays(appliedOn, OUTCOME_EXPOSURE_WEEKS * 7);
}

// ---------------------------------------------------------------------------
// Qué se evalúa
// ---------------------------------------------------------------------------

/**
 * Datos mínimos para no concluir nada con muy poco. CONVENCIÓN DECLARADA.
 *
 * Se declaran aquí y no se esconden en la función porque son justo los números
 * que deciden cuándo la respuesta es «no se puede saber», y esa respuesta es la
 * que más se tiende a saltar.
 */
export const OUTCOME_RULES = {
  /**
   * Semanas con registro, antes Y después. Con menos, `performanceTrend` ni
   * siquiera calcula tendencia, y una comparación entre dos puntos sueltos es
   * ruido: un mal día cambia el veredicto.
   */
  minWeeksEachSide: 2,
  /**
   * Proporción de sesiones hechas en la ventana posterior. Por debajo, lo que
   * se mide es haber faltado, no el cambio de volumen.
   */
  minAdherence: 0.7,
} as const;

export type OutcomeVerdict =
  /** El rendimiento del músculo sube tras el cambio y no subía antes. */
  | 'associated_improvement'
  /** No hay diferencia apreciable. */
  | 'no_clear_change'
  /** El rendimiento baja tras el cambio. */
  | 'associated_decline'
  /** Los datos no permiten decir nada. El caso más frecuente, y legítimo. */
  | 'inconclusive';

export interface OutcomeInput {
  /** e1RM semanal medio de los ejercicios del músculo, ANTES del cambio. */
  beforeWeeklyE1rm: readonly number[];
  /** Lo mismo, DESPUÉS. */
  afterWeeklyE1rm: readonly number[];
  /** Sesiones hechas / planificadas en la ventana posterior. `null` si no se sabe. */
  adherenceAfter: number | null;
  /**
   * Los ejercicios del músculo cambiaron entre las dos ventanas.
   *
   * El §10 lo exige: «compara rendimientos bajo condiciones razonablemente
   * equivalentes; cuando cambien, reduce la comparabilidad». Un press de banca
   * antes y una máquina después no dicen nada uno del otro.
   */
  exercisesChanged: boolean;
  /** Hubo una descarga en la ventana posterior: el e1RM baja a propósito. */
  deloadInWindow: boolean;
}

export interface Outcome {
  verdict: OutcomeVerdict;
  /** Tendencias medidas, para que el «¿Por qué?» no tenga que recalcularlas. */
  before: PerformanceTrend;
  after: PerformanceTrend;
  /**
   * Lo que puede explicar el resultado además del cambio (§10: factores de
   * confusión). Se guardan SIEMPRE, también con un veredicto claro, porque un
   * «asociado a mejora» con una semana de vacaciones en medio no es lo mismo
   * que uno limpio.
   */
  confounders: string[];
  /** Por qué es `inconclusive`, si lo es. Vacío en los demás casos. */
  inconclusiveBecause: string[];
}

/**
 * Juzga un cambio aplicado a partir del rendimiento de antes y de después.
 *
 * LA COMPARACIÓN ES DE TENDENCIAS, no de dos números. «El e1RM medio subió 2 kg»
 * dice poco si ya venía subiendo 2 kg por semana: eso no es respuesta al
 * cambio, es la inercia. Lo que se pregunta es si la PENDIENTE cambió.
 */
export function evaluateOutcome(input: OutcomeInput): Outcome {
  const before = performanceTrend(input.beforeWeeklyE1rm);
  const after = performanceTrend(input.afterWeeklyE1rm);
  const confounders: string[] = [];
  const inconclusiveBecause: string[] = [];

  const semanasAntes = input.beforeWeeklyE1rm.filter((v) => v > 0).length;
  const semanasDespues = input.afterWeeklyE1rm.filter((v) => v > 0).length;

  if (semanasAntes < OUTCOME_RULES.minWeeksEachSide) {
    inconclusiveBecause.push('pocas semanas registradas antes del cambio');
  }
  if (semanasDespues < OUTCOME_RULES.minWeeksEachSide) {
    inconclusiveBecause.push('pocas semanas registradas después del cambio');
  }
  if (input.exercisesChanged) {
    inconclusiveBecause.push('los ejercicios cambiaron: los rendimientos no son comparables');
  }
  if (input.adherenceAfter !== null && input.adherenceAfter < OUTCOME_RULES.minAdherence) {
    inconclusiveBecause.push('se hicieron pocas de las sesiones planificadas');
  }
  if (input.adherenceAfter === null) confounders.push('no se sabe cuántas sesiones se hicieron');

  // Una descarga en medio no invalida por sí sola —la tendencia puede leerse
  // en las semanas de carga— pero siempre se dice: baja el e1RM a propósito.
  if (input.deloadInWindow) confounders.push('hubo una descarga en la ventana');

  // Siempre está: el sueño, el estrés o la comida del usuario no los mide la
  // aplicación, y un veredicto que no lo dijera los daría por controlados.
  confounders.push('sueño, estrés y alimentación no están medidos');

  if (inconclusiveBecause.length > 0 || before === 'unknown' || after === 'unknown') {
    if (before === 'unknown' || after === 'unknown') {
      if (!inconclusiveBecause.some((m) => m.startsWith('pocas'))) {
        inconclusiveBecause.push('no hay tendencia calculable');
      }
    }
    return { verdict: 'inconclusive', before, after, confounders, inconclusiveBecause };
  }

  const orden: Record<Exclude<PerformanceTrend, 'unknown'>, number> = {
    falling: 0,
    flat: 1,
    rising: 2,
  };
  const diferencia = orden[after] - orden[before];

  const verdict: OutcomeVerdict =
    diferencia > 0 ? 'associated_improvement' : diferencia < 0 ? 'associated_decline' : 'no_clear_change';

  return { verdict, before, after, confounders, inconclusiveBecause };
}

/**
 * Cómo se dice un veredicto, con el lenguaje que el §10 exige.
 *
 * «asociado a», «sin cambio claro», «no se puede saber». Nunca «funcionó» ni
 * «causó». Vive en el dominio porque es parte de la decisión —qué se puede
 * afirmar con estos datos— y no de su presentación.
 */
export const OUTCOME_LABELS: Record<OutcomeVerdict, string> = {
  associated_improvement: 'asociado a mejor rendimiento',
  no_clear_change: 'sin cambio claro en el rendimiento',
  associated_decline: 'asociado a peor rendimiento',
  inconclusive: 'no se puede saber con estos datos',
};
